import React, { useState } from 'react';
import { 
  Typography, 
  Box, 
  IconButton, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Card,
  CardContent,
  CardActionArea,
  useMediaQuery
} from '@mui/material';
import * as Icons from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material';

function CocktailMenus({ menus, onCreateMenu }) {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newMenuName, setNewMenuName] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleCreateMenu = () => {
    if (newMenuName.trim()) {
      onCreateMenu({
        name: newMenuName.trim(),
        created: new Date(),
        updated: new Date()
      });
      setNewMenuName('');
      setIsCreateDialogOpen(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleCreateMenu();
    }
  };

  return (
    <Box sx={{ 
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
      minHeight: '100%',
      p: { xs: 1, sm: 0 },
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: { xs: 2, sm: 3 } // Adjusted mb to provide balanced spacing
      }}>
        <Typography variant="h5">My Cocktail Menus</Typography> {/* Re-added header */}
        <IconButton 
          onClick={() => setIsCreateDialogOpen(true)}
          color="primary"
          size={isMobile ? "medium" : "small"}
        >
          <Icons.Add />
        </IconButton>
      </Box>

      {menus.length === 0 ? (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          py: { xs: 8, sm: 6 },
          px: 2,
        }}>
          <Icons.LocalBar 
            sx={{ 
              fontSize: { xs: 64, sm: 48 },
              color: 'text.secondary',
              mb: 2,
              opacity: 0.7
            }}
          />
          <Typography 
            variant="h6" 
            color="text.secondary"
            sx={{ mb: 2, fontWeight: 'normal' }}
          >
            You don't have any cocktail menus yet
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsCreateDialogOpen(true)}
            startIcon={<Icons.Add />}
            size={isMobile ? "large" : "medium"}
          >
            Create Your First Menu
          </Button>
        </Box>
      ) : (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: {
            xs: 'repeat(auto-fill, minmax(140px, 1fr))',
            sm: 'repeat(auto-fill, minmax(200px, 1fr))',
            md: 'repeat(auto-fill, minmax(250px, 1fr))'
          },
          gap: { xs: 1.5, sm: 2 }
        }}>
          {menus.map(menu => (
            <Card 
              key={menu.id} 
              sx={{ 
                borderRadius: { xs: 2, sm: 1 },
                display: 'flex',
                flexDirection: 'column',
                height: { xs: '110px', sm: 'auto' },
                boxShadow: { 
                  xs: '0 2px 8px rgba(0,0,0,0.1)', 
                  sm: 1 
                },
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
            >
              <CardActionArea 
                component={Link} 
                to={`/cocktail-menu/${menu.id}`}
                sx={{ 
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  borderRadius: { xs: 2, sm: 1 },
                }}
              >
                <CardContent sx={{ width: '100%', p: { xs: 2, sm: 2 } }}>
                  <Typography 
                    variant="h6"
                    sx={{ 
                      fontSize: { xs: '1.1rem', sm: '1.25rem' },
                      mb: 0.5,
                      fontWeight: 500,
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                    }}
                  >
                    {menu.name}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      fontSize: { xs: '0.85rem', sm: '0.875rem' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Icons.LocalBar fontSize="small" />
                    {menu.recipes?.length || 0} recipes
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}

      <Dialog 
        open={isCreateDialogOpen} 
        onClose={() => setIsCreateDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { 
            width: { xs: '95%', sm: '100%' }, 
            mx: 'auto',
            borderRadius: { xs: 2, sm: 1 }
          }
        }}
      >
        <DialogTitle sx={{ 
          py: { xs: 2, sm: 1.5 },
          fontSize: { xs: '1.25rem', sm: '1.5rem' }
        }}>
          Create New Cocktail Menu
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Menu Name"
            fullWidth
            value={newMenuName}
            onChange={(e) => setNewMenuName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="e.g., Summer Party, Favorites, Tiki Night"
            sx={{
              '& .MuiInputBase-root': {
                borderRadius: { xs: 2, sm: 1 },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 1.5 } }}>
          <Button 
            onClick={() => setIsCreateDialogOpen(false)}
            sx={{ minWidth: { xs: '80px', sm: '64px' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateMenu} 
            color="primary"
            variant="contained"
            sx={{ minWidth: { xs: '80px', sm: '64px' } }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CocktailMenus;